import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Paperclip, DownloadSimple, Trash, Upload, FileImage, FilePdf, FileXls, FileDoc, File } from "@phosphor-icons/react";

/**
 * CaseAttachments — per-case supporting documents panel.
 * Drops into the CaseDetail page. Stores uploads in GridFS (any file type, up to 50 MB).
 *
 * Props:
 *   caseId — the case_id (or legacy_reference_no / our_file_reference / case_code; backend resolves)
 */
const ICONS = {
  image: FileImage,
  pdf: FilePdf,
  excel: FileXls,
  doc: FileDoc,
  other: File,
};

function pickIcon(mime, filename) {
  const m = (mime || "").toLowerCase();
  const n = (filename || "").toLowerCase();
  if (m.startsWith("image/")) return ICONS.image;
  if (m.includes("pdf") || n.endsWith(".pdf")) return ICONS.pdf;
  if (m.includes("spreadsheet") || /\.(xlsx|xls|csv)$/.test(n)) return ICONS.excel;
  if (m.includes("word") || /\.(docx|doc)$/.test(n)) return ICONS.doc;
  return ICONS.other;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function CaseAttachments({ caseId }) {
  const { user } = useAuth();
  const canDelete = (user?.ux_tier || 0) >= 4;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/attachments`);
      setItems(data.attachments || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (caseId) load(); /* eslint-disable-next-line */ }, [caseId]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      setErr("File exceeds 50 MB cap.");
      return;
    }
    uploadFile(f);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setProgress(0);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/cases/${caseId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/cases/${caseId}/attachments/${att.attachment_id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: att.content_type || "application/octet-stream" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = att.filename || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"? This is a soft-delete and can be restored from audit log.`)) return;
    try {
      await api.delete(`/cases/${caseId}/attachments/${att.attachment_id}`);
      await load();
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-3" data-testid="case-attachments">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Paperclip size={16} weight="duotone" className="text-[var(--brand-primary)]" />
          <h3 className="font-bold text-[14px]">Supporting documents <span className="text-[11px] font-normal text-[var(--text-muted)]">· {items.length}</span></h3>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" onChange={onPick} className="hidden" data-testid="case-attachment-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="case-attachment-upload"
            className="span-btn span-btn-ghost h-8 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Upload size={13} /> {uploading ? `Uploading… ${progress}%` : "Upload file"}
          </button>
        </div>
      </div>

      <div className="text-[11px] text-[var(--text-muted)]">
        Accepts any file type (images, PDF, Excel, Word, PPT, CSV, etc.). 50 MB cap per file. Stored in encrypted GridFS — migrates to S3 once cloud storage is provisioned.
      </div>

      {err && (
        <div className="text-[12px] text-red-800 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="case-attachment-error">{String(err)}</div>
      )}

      {uploading && (
        <div className="h-1.5 bg-[var(--surface-2)] rounded overflow-hidden" data-testid="case-attachment-progress">
          <div className="h-full bg-[var(--brand-primary)] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-[var(--text-muted)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[12px] text-[var(--text-muted)] bg-[var(--surface-2)] border border-dashed border-[var(--border)] rounded px-3 py-6 text-center" data-testid="case-attachment-empty">
          No supporting documents yet — click <strong>Upload file</strong> to add the first one.
        </div>
      ) : (
        <ul className="border border-[var(--border)] rounded divide-y divide-[var(--border)]" data-testid="case-attachment-list">
          {items.map(att => {
            const Icon = pickIcon(att.content_type, att.filename);
            return (
              <li key={att.attachment_id} className="flex items-center justify-between gap-3 px-3 py-2 text-[12px]" data-testid={`attachment-${att.attachment_id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Icon size={18} weight="duotone" className="text-[var(--brand-primary)] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{att.filename}</div>
                    <div className="text-[10px] text-[var(--text-muted)]">
                      {fmtSize(att.size_bytes)} · {att.content_type || "—"} · uploaded {fmtDate(att.uploaded_at)} by {att.uploaded_by_name || "—"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => download(att)} title="Download"
                    data-testid={`attachment-download-${att.attachment_id}`}
                    className="p-1.5 hover:bg-[var(--surface-2)] rounded">
                    <DownloadSimple size={14} />
                  </button>
                  {canDelete && (
                    <button onClick={() => remove(att)} title="Delete (tier 4+)"
                      data-testid={`attachment-delete-${att.attachment_id}`}
                      className="p-1.5 hover:bg-red-50 text-red-600 rounded">
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
